/**
 * Notification panel for challenge creators.
 * Shows flagged submissions that need review.
 * Creator can approve or remove each submission.
 */

import { AlertCircle, CheckCircle, EyeOff, Trash2, X } from 'lucide-react';
import { useState } from 'react';
import type { NotificationDetail } from '@jemmi/schemas';

import { MediaLightbox } from '@/components/MediaLightbox';

type Props = {
  notification: NotificationDetail;
  onApprove: (notificationId: string) => void;
  onRemove: (notificationId: string) => void;
  onClose: (notificationId: string) => void;
};

/**
 * Displays a single flagged submission notification.
 * Shows submission preview, flag reason, and action buttons.
 */
export function NotificationPanel({ notification, onApprove, onRemove, onClose }: Props) {
  const [lightboxOpen, setLightboxOpen] = useState(false);

  // 'submission_flagged' notifications always have a submission attached;
  // this guard is just for the type checker (submission is nullable on the
  // shared schema since other notification types don't have one).
  if (!notification.submission) return null;
  const { submission } = notification;

  if (notification.actionTaken) {
    return (
      <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
        <div className="flex items-start gap-3">
          <button
            type="button"
            onClick={() => setLightboxOpen(true)}
            aria-label="View flagged content (blurred by default)"
            className="relative h-12 w-12 flex-shrink-0 overflow-hidden rounded-lg"
          >
            <img
              src={submission.mediaUrl}
              alt="Submission"
              className="h-full w-full scale-110 object-cover blur-md"
            />
            <div className="absolute inset-0 flex items-center justify-center bg-black/40">
              <EyeOff className="h-4 w-4 text-white" />
            </div>
          </button>
          <div className="flex-1">
            <p className="flex items-center gap-1.5 font-semibold text-slate-900">
              {notification.actionTaken === 'approved' ? (
                <CheckCircle className="h-4 w-4 flex-shrink-0 text-green-600" />
              ) : (
                <Trash2 className="h-4 w-4 flex-shrink-0 text-red-600" />
              )}
              You {notification.actionTaken === 'approved' ? 'approved' : 'removed'} this submission
            </p>
            <p className="mt-1 text-sm text-slate-500">
              {new Date(notification.actionTakenAt || '').toLocaleString()}
            </p>
          </div>
          <button
            type="button"
            onClick={() => onClose(notification.id)}
            className="text-slate-400 hover:text-slate-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
        {lightboxOpen && (
          <MediaLightbox submission={submission} onClose={() => setLightboxOpen(false)} />
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3 rounded-xl border border-amber-200 bg-amber-50 p-4">
      {/* Header */}
      <div className="flex items-start gap-3">
        <AlertCircle className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-600" />
        <div className="min-w-0 flex-1">
          <p className="font-semibold text-slate-900">
            Submission flagged in "{notification.challenge.title}"
          </p>
          <p className="mt-1 text-sm text-slate-600">
            {notification.flagReportCount}{' '}
            {notification.flagReportCount === 1 ? 'report' : 'reports'}
          </p>
        </div>
      </div>

      {/* Submission preview */}
      <div className="flex gap-3 rounded-lg bg-white p-3">
        <button
          type="button"
          onClick={() => setLightboxOpen(true)}
          aria-label="View flagged content (blurred by default)"
          className="relative h-16 w-16 flex-shrink-0 overflow-hidden rounded-lg"
        >
          <img
            src={submission.mediaUrl}
            alt="Submission"
            className="h-full w-full scale-110 object-cover blur-md"
          />
          <div className="absolute inset-0 flex items-center justify-center bg-black/40">
            <EyeOff className="h-5 w-5 text-white" />
          </div>
        </button>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-slate-900">
            By {submission.user.displayName}
          </p>
          {submission.caption && (
            <p className="mt-1 line-clamp-2 text-sm text-slate-600">{submission.caption}</p>
          )}
        </div>
      </div>

      {/* Content guidelines reminder */}
      <div className="rounded-lg bg-white p-3 text-sm text-slate-700">
        <p className="mb-1 font-medium">📋 Content Guidelines Reminder</p>
        <p>
          All submissions must adhere to our content guidelines. Please review this submission and
          decide whether to approve (keep) or remove (delete) it from your challenge.
        </p>
      </div>

      {/* Action buttons */}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => onApprove(notification.id)}
          className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-green-600 px-3 py-2 text-sm font-semibold text-white transition-colors hover:bg-green-700"
        >
          <CheckCircle className="h-4 w-4" />
          Approve
        </button>
        <button
          type="button"
          onClick={() => onRemove(notification.id)}
          className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-red-600 px-3 py-2 text-sm font-semibold text-white transition-colors hover:bg-red-700"
        >
          <Trash2 className="h-4 w-4" />
          Remove
        </button>
      </div>

      {lightboxOpen && (
        <MediaLightbox submission={submission} onClose={() => setLightboxOpen(false)} />
      )}
    </div>
  );
}
